import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import { NavigateFunction } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { SeniorityFormData } from '@/modules/settings/types/seniority';
import { SenioritiesFormData } from '@/modules/settings/validations/seniority-validation';
import { AuthenticatedRoutes } from '@/router/routes';

interface GetColumnsParams {
  onDelete: (seniority: SeniorityFormData, index: number) => void;
  control: Control<SenioritiesFormData>;
  t: TFunction;
  navigate: NavigateFunction;
}

export const getSenioritiesColumns = ({
  onDelete,
  control,
  t,
  navigate,
}: GetColumnsParams): ColumnDef<SeniorityFormData>[] => [
  {
    accessorKey: 'name',
    header: () => {
      return <div style={{ minWidth: '16rem' }}>{t('pages.seniorities.nameColumn')}</div>;
    },
    cell: ({ row }) => {
      const index = row.index;

      return (
        <FormField
          control={control}
          name={`seniorities.${index}.name`}
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('pages.seniorities.namePlaceholder')}
                  className={cn(
                    fieldState.error && 'border-destructive focus-visible:ring-destructive'
                  )}
                />
              </FormControl>
              {fieldState.error && (
                <FormMessage>{t(fieldState.error.message as string)}</FormMessage>
              )}
            </FormItem>
          )}
        />
      );
    },
  },
  {
    accessorKey: 'memberCount',
    header: () => {
      return <div style={{ minWidth: '12rem' }}>{t('pages.seniorities.membersColumn')}</div>;
    },
    cell: ({ row }) => {
      const seniority = row.original;
      const hasMembers = seniority.memberCount > 0;

      const isNewSeniority = seniority.seniorityId < 0;

      return (
        <Button
          type="button"
          variant="ghost"
          disabled={!hasMembers || isNewSeniority}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            if (hasMembers && !isNewSeniority) {
              navigate(`${AuthenticatedRoutes.Team}?seniority=${seniority.seniorityId}`);
            }
          }}
          className="h-auto w-full justify-start rounded-lg px-3 py-2"
        >
          {seniority.memberCount}
        </Button>
      );
    },
  },
  {
    id: 'actions',
    size: 80,
    header: () => {
      return <div className="flex w-full justify-end">{t('pages.seniorities.actionsColumn')}</div>;
    },
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original, row.index)}
            className="h-8 w-8"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    },
  },
];
